import EmptyState from '@pinecast/common/EmptyState';
import {defineMessages, FormattedMessage} from '@pinecast/i18n';
import {useCSS} from '@pinecast/styles';
import * as React from 'react';

import TranscriptUtteranceEditor from './TranscriptUtteranceEditor';
import TranscriptUtteranceSpeakerEditor from './TranscriptUtteranceSpeakerEditor';
import type {UtteranceData} from './types';

const messages = defineMessages({
  emptyStateTitle: {
    id: 'tr-reader.TranscriptReader.emptyStateTitle',
    description: 'Title for the empty state when there is no transcript data',
    defaultMessage: 'No transcript data',
  },
  emptyStateCopy: {
    id: 'tr-reader.TranscriptReader.emptyStateCopy',
    description: 'Copy for the empty state when there is no transcript data',
    defaultMessage:
      'The transcript shows that there was no spoken content in the episode.',
  },
});

type Props = {
  onEditUtterance?: (text: string, index: number) => Promise<void>;
  onEditUtteranceSpeaker?: (speaker: string, index: number) => Promise<void>;
  onMergeUtterance?: (index: number) => Promise<void>;
  onSplitUtterance?: (index: number) => Promise<void>;
  utteranceData: UtteranceData;
  speakerMap?: Record<string, string>;
  showSpeakerLabels?: boolean;
};

const TranscriptReader = ({
  onEditUtterance,
  onEditUtteranceSpeaker,
  onMergeUtterance,
  onSplitUtterance,
  utteranceData,
  showSpeakerLabels,
  speakerMap,
}: Props) => {
  const css = useCSS();

  // Show the speakers if:
  // 1. We're asked to show the speaker labels
  // 2. We have a speaker map with at least one speaker
  // 3. There are at least two speakers in the transcript
  // We don't show speakers if there is only one speaker in the transcript
  const showSpeakers =
    !!showSpeakerLabels ||
    (speakerMap && Object.keys(speakerMap).length > 0) ||
    new Set(utteranceData.map(u => u.speaker)).size > 1;

  const allSpeakerLabels = React.useMemo(
    () => Array.from(new Set(utteranceData.map(u => u.speaker))),
    [utteranceData],
  );

  return utteranceData.length ? (
    <div
      className={css({
        display: 'grid',
        columnGap: '8px',
        rowGap: '8px',
        gridTemplateColumns: showSpeakers ? 'max-content 1fr' : '1fr',
      })}
    >
      {utteranceData.map((utterance, i) => {
        // Can merge if this utterance has the same speaker as the previous one
        const canMergeWithPrevious =
          i > 0 && utterance.speaker === utteranceData[i - 1].speaker;

        return (
          <React.Fragment key={utterance.start}>
            {showSpeakers && (
              <TranscriptUtteranceSpeakerEditor
                speaker={utterance.speaker}
                index={i}
                speakerMap={speakerMap}
                allSpeakerLabels={allSpeakerLabels}
                showSpeakerLabels={!!showSpeakerLabels}
                onEdit={onEditUtteranceSpeaker}
                canMergeWithPrevious={canMergeWithPrevious}
                onMerge={
                  onMergeUtterance ? () => onMergeUtterance(i) : undefined
                }
              />
            )}
            <TranscriptUtteranceEditor
              text={utterance.text}
              index={i}
              onEdit={onEditUtterance}
              onSplit={onSplitUtterance ? () => onSplitUtterance(i) : undefined}
            />
          </React.Fragment>
        );
      })}
    </div>
  ) : (
    <EmptyState
      title={<FormattedMessage {...messages.emptyStateTitle} />}
      copy={<FormattedMessage {...messages.emptyStateCopy} />}
    />
  );
};

export default TranscriptReader;
